import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { MaskLines, Reveal } from "@/components/Reveal";
import { IMAGES, INFO } from "@/data/site";
import { api } from "@/lib/api";

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const inputCls =
  "w-full bg-transparent border-b border-line py-3 font-sans text-lg text-ink placeholder:text-muted/50 focus:border-harissa focus:outline-none transition-colors";

export default function Reservation() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", time: "19:00", guests: 2, message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.date) {
      toast.error("Bitte Name, E-Mail und Datum ausfüllen.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/reservations", { ...form, guests: Number(form.guests) });
      setDone(true);
      toast.success("Anfrage gesendet! Wir melden uns.");
    } catch (err) {
      toast.error("Etwas ist schiefgelaufen. Versuch es nochmal.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reservation-page" className="pt-28 sm:pt-36 min-h-screen">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Left visual */}
        <div className="hidden lg:block relative border-r border-line">
          <div className="sticky top-20 h-[calc(100vh-5rem)]">
            <img src={IMAGES.jollof} alt="Banga Bowls" className="w-full h-full object-cover contrast-125 grayscale-[15%]" />
            <div className="absolute inset-0 bg-base/30" />
            <div className="absolute bottom-10 left-10 right-10">
              <h2 className="font-display uppercase text-7xl leading-[0.85] glow-harissa">Tisch<br/>anfragen.</h2>
              <p className="font-mono text-sm text-ink/80 mt-4">{INFO.city} · {INFO.hours}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="px-5 sm:px-8 lg:px-14 py-14">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-harissa mb-5">Reservierung</p>
          <h1 className="font-display uppercase text-[15vw] sm:text-6xl leading-[0.85] mb-4 lg:hidden">
            <MaskLines lines={["Tisch anfragen."]} />
          </h1>
          <p className="font-sans text-muted max-w-md mb-10">
            Sag uns, wann und mit wie vielen. Wir bestätigen per E-Mail und halten die Box warm.
          </p>

          {done ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-plantain p-10 text-center" data-testid="reservation-success">
              <div className="w-16 h-16 border border-plantain mx-auto flex items-center justify-center mb-6">
                <Check className="text-plantain" size={32} />
              </div>
              <h2 className="font-head font-[800] text-3xl uppercase">Danke, {form.name.split(" ")[0]}!</h2>
              <p className="font-sans text-muted mt-4">Deine Anfrage ist bei uns. Wir melden uns so schnell wie möglich.</p>
              <button onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", date: "", time: "19:00", guests: 2, message: "" }); }} className="font-mono text-xs uppercase tracking-widest mt-8 border border-line px-6 py-3 hover:border-harissa hover:text-harissa transition-colors" data-testid="reservation-again-btn">
                Neue Anfrage
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-8 max-w-lg" data-testid="reservation-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <Field label="Name *" testid="field-name">
                  <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Dein Name" data-testid="input-name" />
                </Field>
                <Field label="Telefon" testid="field-phone">
                  <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+41 …" data-testid="input-phone" />
                </Field>
              </div>
              <Field label="E-Mail *" testid="field-email">
                <input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="du@mail.ch" data-testid="input-email" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                <Field label="Datum *" testid="field-date">
                  <input type="date" className={`${inputCls} [color-scheme:dark]`} value={form.date} onChange={set("date")} data-testid="input-date" />
                </Field>
                <Field label="Zeit" testid="field-time">
                  <input type="time" className={`${inputCls} [color-scheme:dark]`} value={form.time} onChange={set("time")} data-testid="input-time" />
                </Field>
                <Field label="Personen" testid="field-guests">
                  <input type="number" min="1" max="30" className={inputCls} value={form.guests} onChange={set("guests")} data-testid="input-guests" />
                </Field>
              </div>
              <Field label="Nachricht" testid="field-message">
                <textarea rows="3" className={inputCls + " resize-none"} value={form.message} onChange={set("message")} placeholder="Allergien, Anlass, Wünsche…" data-testid="input-message" />
              </Field>

              <button type="submit" disabled={loading} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-harissa text-base font-mono text-sm uppercase tracking-widest px-10 py-4 hover:bg-ink transition-colors disabled:opacity-60" data-testid="reservation-submit-btn">
                {loading ? <><Loader2 size={16} className="animate-spin" /> Sende…</> : "Anfrage senden"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
